import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskedLines, Overline } from "../components/motion";
import { IMAGES, HERO_STATS } from "../data";

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} id="hero" className="relative min-h-screen w-full overflow-hidden bg-bg" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-brand-green/10 blur-[130px] pointer-events-none" />
      <div className="absolute top-1/3 -left-40 w-[30rem] h-[30rem] rounded-full bg-brand-bright/10 blur-[120px] pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 pt-32 md:pt-36 pb-20 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center w-full">
          {/* Left */}
          <motion.div style={{ y }} className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <Overline className="flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-brand-green" />
                Ahmedabad · Established 1983 · RBI-Registered NBFC
              </Overline>
            </motion.div>

            <h1 className="mt-7 font-heading font-extrabold tracking-tighter text-ink text-[13vw] sm:text-[9vw] lg:text-[6.4vw] leading-[0.94]">
              <MaskedLines lines={["Capital for India's"]} start={0.3} />
              <MaskedLines lines={["Green Transition"]} start={0.45} lineClassName="text-brand-green" />
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9 }} className="mt-8 max-w-xl font-body text-base md:text-lg text-ink/60 leading-relaxed">
              Four decades of financial-services discipline, now channelled into a specialised platform for electric mobility, charging infrastructure and sustainable growth.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button onClick={() => navigate("/contact")} data-testid="hero-cta-primary" className="inline-flex items-center gap-2 font-heading text-sm font-semibold tracking-wide bg-brand-green text-white px-9 py-4 hover:bg-ink transition-colors duration-300">Partner With Us <ArrowRight size={16} /></button>
              <button onClick={() => navigate("/green-finance")} data-testid="hero-cta-secondary" className="font-heading text-sm font-semibold tracking-wide border border-ink/25 text-ink px-9 py-4 hover:bg-ink hover:text-white transition-colors duration-300">The ₹190 Cr Architecture</button>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} className="mt-14 grid grid-cols-3 gap-px bg-ink/10 border-y border-ink/10 max-w-2xl">
              {HERO_STATS.map((s) => (
                <div key={s.l} className="bg-bg px-4 py-5">
                  <div className="font-heading font-extrabold text-2xl md:text-3xl text-ink tracking-tight">{s.v}</div>
                  <div className="mt-1 font-body text-[10px] md:text-[11px] text-ink/50 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right — framed bus */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-5 relative">
            <div className="absolute -top-4 -right-4 w-full h-full border border-brand-green/30 hidden lg:block" />
            <div className="relative overflow-hidden bg-surface border border-ink/10 group">
              <motion.img style={{ y: yImg }} src={IMAGES.heroGreen} alt="India's green transition" className="w-full h-[300px] lg:h-[440px] object-cover scale-105" />
              <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-brand-green via-brand-green/60 to-transparent">
                <div className="overline text-white/80">Financing India's Green Transition</div>
                <div className="font-heading font-bold text-white text-lg mt-1">Electric mobility · Charging · Clean energy</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-8 right-6 md:right-12 lg:right-20 z-10 flex items-center gap-2 text-ink/40">
        <span className="overline text-[10px]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}><ArrowDown size={16} /></motion.span>
      </motion.div>
    </section>
  );
};
